// FOUND-11: Composes the embed harness — emits neko-widget-ready + height-sync.
// Phase 2: accepts { tenantId, tenantSlug } and threads tenant_id into every postMessage envelope.
import { postToParent as defaultPostToParent, type EmbedMessage } from "./postMessage";
import { resolveTargetOrigin } from "./allowed-origins";
import { startHeightSync } from "./height-sync";

declare const __APP_VERSION__: string;

interface HarnessOptions {
  tenantId?: string;
  tenantSlug?: string;
  postToParent?: (msg: EmbedMessage, targetOrigin?: string) => void;
  version?: string;
  targetOrigin?: string | null;
}

export function startEmbedHarness(opts: HarnessOptions = {}): void {
  const tenantId = opts.tenantId ?? "";

  const version =
    opts.version ??
    (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0");

  const target =
    opts.targetOrigin !== undefined ? opts.targetOrigin : resolveTargetOrigin();

  // If a custom postToParent is supplied (test injection), use it directly.
  if (opts.postToParent) {
    opts.postToParent({ type: "neko-widget-ready", tenant_id: tenantId, version });
    if (typeof document !== "undefined" && document.body) {
      try {
        startHeightSync(document.body, (height) =>
          opts.postToParent!({ type: "neko-widget-height", tenant_id: tenantId, height }),
        );
      } catch {
        // ResizeObserver may not exist in some jsdom contexts; harmless.
      }
    }
    return;
  }

  // Production path: only run inside an iframe with a resolvable parent origin.
  if (typeof window === "undefined") return;
  if (window.self === window.top) return;
  if (!target) {
    // Stop-gap: do NOT post to wildcard. Render normally.
    return;
  }

  try {
    startHeightSync(document.body, (height) =>
      defaultPostToParent({ type: "neko-widget-height", tenant_id: tenantId, height }, target),
    );
  } catch {
    // ignore observer errors
  }
  defaultPostToParent({ type: "neko-widget-ready", tenant_id: tenantId, version }, target);
}
